import { titleColumn } from "@/components/NewAndIncredibleChatWidget/approval/approvalValues";
import { UUID_RE } from "@/utils/generic";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";

// Ids read in one lookup; a reference column on one page holds at most this.
const MAX_IDS = 200;

type Column = { name: string; label?: string | null };

// A column that names its row (an invoice number, a reference code); the
// table's own ``id`` is the raw key the reference already holds.
const IDENTIFIER = /(^|[_\s])(id|ref|reference|number|no|code|key)$/i;

const words = (text: string) =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/** The column that tells one row of ``tableName`` from another: of the
 * id-like columns (the table's own ``id`` aside), one named after the table
 * ("billing_invoice_id" on Invoices), else one whose values differ across
 * ``rows``, else the first. A company's tax id on an invoice names the
 * company, not the invoice. */
export function identifierColumn(
  columns: Column[],
  tableName: string,
  rows: Record<string, unknown>[]
): Column | undefined {
  const candidates = columns.filter(
    (c) =>
      c.name !== "id" &&
      (IDENTIFIER.test(c.name) || IDENTIFIER.test(c.label ?? ""))
  );
  const stems = words(tableName).map((w) => w.replace(/s$/, ""));
  const named = candidates.find((c) =>
    words(`${c.name} ${c.label ?? ""}`).some((w) => stems.includes(w))
  );
  if (named) return named;
  const distinct = candidates.find(
    (c) =>
      rows.length > 1 &&
      new Set(rows.map((r) => r[c.name])).size === rows.length
  );
  return distinct ?? candidates[0];
}

/** How a linked row reads: its identifying column with its title-like one
 * ("INV-004 · COMPANY D LDA"), or whichever of the two it has. */
export function referenceLabel(
  columns: Column[],
  ident: Column | undefined,
  row: Record<string, unknown>
): string | undefined {
  const shown = (c: Column | undefined) => {
    const value = c ? row[c.name] : undefined;
    return value == null || value === "" ? undefined : String(value);
  };
  const title = columns[titleColumn(columns.map((c) => c.label || c.name))];
  const parts = [shown(ident), title === ident ? undefined : shown(title)];
  const label = parts.filter(Boolean).join(" · ");
  return label || undefined;
}

/** Each row id in ``ids`` of table ``tableId`` as ``referenceLabel`` reads
 * it. Every cell of one column passes the same ``ids``, so the lookup is
 * one request per column, shared by its cells. */
export function useReferenceTitles(
  tableId: string,
  ids: string[]
): Map<string, string> {
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  const valid = [...new Set(ids)]
    .filter((id) => UUID_RE.test(id) || /^\d+$/.test(id))
    .sort()
    .slice(0, MAX_IDS);
  const table = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: g, table_id: tableId } },
    { enabled: !!g && valid.length > 0 }
  ).data;
  const rows = useRemoteData(
    DATA_KEYS.TABLE_ROWS,
    {
      path: { group_id: g, table_id: tableId },
      query: {
        limit: MAX_IDS,
        filters: JSON.stringify([{ field: "id", op: "in", value: valid }]),
      },
    },
    { enabled: !!g && valid.length > 0 }
  ).data?.rows;
  const titles = new Map<string, string>();
  const columns = table?.columns ?? [];
  if (!columns.length || !rows) return titles;
  const ident = identifierColumn(columns, table?.name ?? "", rows);
  for (const row of rows) {
    const label = referenceLabel(columns, ident, row);
    if (label) titles.set(String(row.id), label);
  }
  return titles;
}

/** A reference cell as the row it links to: its title, the id until known. */
export function ReferenceValue({
  tableId,
  id,
  ids,
}: {
  tableId: string;
  id: string;
  ids: string[];
}) {
  const title = useReferenceTitles(tableId, ids).get(id);
  return title ? <>{title}</> : <span className="font-mono text-xs">{id}</span>;
}
